import { DateTime } from 'luxon'
import React, { useEffect, useState } from 'react'
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import Button from '../../../../../components/atoms/button/Button'
import Text from '../../../../../components/atoms/text/Text'
import DaySelector from '../../../../../components/molecules/daySelector/DaySelector'
import LoadingData from '../../../../../components/molecules/loadingData/LoadingData'
import Screen from '../../../../../components/molecules/screen/Screen'
import routes from '../../../../../config/routes'
import { useProfile } from '../../../../../modules/auth/hooks'
import {
  useAvailableHours,
  useAvailableSlots,
  useReservateMeetingRoomMutation,
} from '../../../../../modules/meetingRoom/hooks'
import { useReservatedSeatById } from '../../../../../modules/seats/hooks'
import { getBookDay, getReservationDay, getReservationEndDay } from '../../../../../modules/seats/utils'
import { useBookAssistant } from '../../../../../providers/bookAssistant'
import { TYPES } from '../../../../../providers/bookAssistant/types'
import { ILocationState } from '../../../../../types/app.types'
import { VARIANTS } from '../../../../../types/Components.types'
import SlotsList from './components/SlotsList'
import styles from './MeetingRoomAvailability.module.css'

const MeetingRoomAvailability: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as ILocationState | null
  const { slotHour } = locationState || {}
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [selectedRange, setSelectedRange] = useState<Date[]>([new Date(), new Date()])
  const { setType, type, setDay, hours, setHours, clear } = useBookAssistant()
  const { data: profile, isLoading: isLoadingProfile } = useProfile()
  const { bookId } = useParams() as { bookId: string }
  const { data: reservation, isLoading: isLoadingReservation } = useReservatedSeatById({
    userId: profile?.id,
    seatId: bookId,
  })
  const { data: slots, isLoading: isLoadingSlots } = useAvailableSlots({
    date: selectedDate ? DateTime.fromJSDate(selectedDate).toISODate()! : undefined,
  })

  const { data: availableHours, isLoading: isLoadingAvailableHours } = useAvailableHours({ userId: profile?.id })

  const totalHours = availableHours ?? 0
  const needToBuyVoucher = totalHours === 0 || hours.length > totalHours

  const minHourAvailable = reservation ? DateTime.fromISO(reservation?.Users_Seats[0]?.reserved_from).hour || 8 : 0
  const maxHourAvailable = reservation ? DateTime.fromISO(reservation?.Users_Seats[0]?.reserved_to).hour - 1 || 21 : 0

  const handleDateChange = (date: Date) => {
    setHours({ hours: [] }) // Reset selected hours when date changes
    setSelectedDate(date)
  }

  const handleSlotSelected = (selectedHour: string) => {
    const idx = hours.findIndex((h) => h === selectedHour)
    // if (hours.length >= totalHours) {
    //   // TODO send toast
    //   return
    // }
    if (idx < 0) {
      setHours({ hours: [...hours, selectedHour] })
    }
  }

  const handleSlotDeselected = (selectedHour: string) => {
    const idx = hours.findIndex((h) => h === selectedHour)
    if (idx >= 0) {
      setHours({ hours: hours.filter((h) => h !== selectedHour) })
    }
  }

  const reservateMeetingRoomMutation = useReservateMeetingRoomMutation()

  const handleContinueClick = () => {
    reservateMeetingRoomMutation.mutate(
      { type, day: selectedDate, hours, bookId, userId: profile!.id },
      {
        onSuccess: ({ status, params }) => {
          if (status === 402) {
            navigate(`${routes.book.vouchers}?b=${params}`)
            return
          }

          clear()
          navigate(routes.book.new.meetingsRoom.success, { replace: true })
        },
      },
    )
  }

  useEffect(() => {
    if (type !== TYPES.MEETING_ROOM) {
      setType({ type: TYPES.MEETING_ROOM })
    }
  }, [selectedDate, setType, setDay, setHours, type])

  useEffect(() => {
    if (!reservation) {
      return
    }

    const type = reservation.type
    let from, to
    if (type === TYPES.DAY) {
      from = getBookDay({ reservation }).toJSDate()
      to = from
    } else {
      from = getReservationDay({ reservation }).toJSDate()
      to = getReservationEndDay({ reservation }).toJSDate()
    }
    setSelectedRange([from, to])
    setSelectedDate(from)
  }, [reservation])

  useEffect(() => {
    if (!slotHour) {
      return
    }

    setHours({ hours: [slotHour] })
  }, [setHours, slotHour])

  useEffect(() => {
    return () => {
      clear()
    }
  }, [clear])

  const isLoading = isLoadingProfile || isLoadingReservation || isLoadingAvailableHours
  if (isLoading) {
    return <LoadingData />
  }

  const [from, to] = selectedRange
  return (
    <Screen
      headerProps={{
        backEnabled: true,
        variant: VARIANTS.pig,
        title: 'Sala de reuniones',
      }}
    >
      <nav className={styles.navigator}>
        <DaySelector
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          minDate={from}
          maxDate={to}
          aria-label="Seleccionar fecha para disponibilidad de sala de reuniones"
        />
      </nav>
      <article className={styles.container}>
        <div className={styles.tipContainer}>
          <div className={styles.tipContent}>
            <Text size="sm" variant={VARIANTS.info} className={styles.tipText}>
              Sólo puedes reservar la sala de reuniones durante el período de tu bono
              {from && to && (
                <>
                  {' '}
                  ({DateTime.fromJSDate(from).toFormat('dd/MM/yyyy')} - {DateTime.fromJSDate(to).toFormat('dd/MM/yyyy')}
                  )
                </>
              )}
            </Text>
          </div>
        </div>

        {isLoadingSlots ? (
          <div className={styles.loadingWrapper}>
            <LoadingData showLogo={false} />
          </div>
        ) : (
          <div className={styles.slotsList}>
            <SlotsList
              slots={slots || []}
              date={selectedDate}
              selectedSlots={hours}
              minSlotDisabled={minHourAvailable}
              maxSlotDisabled={maxHourAvailable}
              onSlotSelected={handleSlotSelected}
              onSlotDeselected={handleSlotDeselected}
              aria-label="Franjas horarias de la sala de reuniones"
            />
          </div>
        )}
      </article>

      <div className={styles.actions}>
        <CircularProgressbar
          className={styles.chart}
          value={totalHours - hours.length}
          maxValue={totalHours}
          text={`${totalHours - hours.length}h`}
          styles={buildStyles({
            pathColor: 'rgba(26, 145, 215)',
            textColor: 'rgba(26, 145, 215)',
            textSize: '26px',
          })}
        />

        <Button
          variant={VARIANTS.primary}
          className={styles.action}
          onClick={handleContinueClick}
          disabled={hours.length === 0}
        >
          {hours.length > 0 ? (needToBuyVoucher ? 'Comprar bono' : 'Reservar') : 'Selecciona las horas'}
        </Button>
      </div>
    </Screen>
  )
}

export default MeetingRoomAvailability
